<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          Edit Weekly Reward
        </v-card-title>

        <v-card-text>
          <!-- Category Information Display -->
          <v-row v-if="!editMode">
            <v-col sm="12">
              <v-card outlined class="mb-4">
                <v-card-title class="subtitle-1 primary white--text">
                  Category Details
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col sm="6">
                      <strong>Category:</strong>
                      {{ weeklyReward.category ? weeklyReward.category.description : 'No Category' }}
                    </v-col>
                    <v-col sm="6">
                      <strong>Status:</strong>
                      <v-chip
                        :color="weeklyReward.is_active ? 'teal' : 'red'"
                        text-color="white"
                        small
                      >
                        {{ weeklyReward.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </v-col>
                  </v-row>
                  <v-row class="mt-2">
                    <v-col sm="12">
                      <v-btn
                        color="primary"
                        small
                        @click="enterEditMode"
                        :disabled="isLoading"
                      >
                        <v-icon left>mdi-pencil</v-icon>
                        Edit Category & Reward
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Current Reward Details (View Mode) -->
          <v-row v-if="!editMode && weeklyReward.id">
            <v-col sm="12">
              <v-card outlined>
                <v-card-title class="subtitle-1 teal white--text">
                  Current Reward Details
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col sm="3">
                      <strong>Name:</strong>
                      {{ weeklyReward.name || 'No Name' }}
                    </v-col>
                    <v-col sm="3">
                      <strong>Coin Value:</strong>
                      <v-chip color="orange" text-color="white" small>
                        <v-icon left small>mdi-bitcoin</v-icon>
                        {{ weeklyReward.coin || 0 }}
                      </v-chip>
                    </v-col>
                    <v-col sm="3">
                      <strong>Store Item:</strong>
                      {{ weeklyReward.store_item_id || 'None' }}
                    </v-col>
                    <v-col sm="3">
                      <strong>Expiry Date:</strong>
                      {{ formatDate(weeklyReward.expiry_date) || 'No Expiry' }}
                    </v-col>
                  </v-row>
                  <v-row v-if="weeklyReward.image_path" class="mt-2">
                    <v-col sm="12">
                      <strong>Image:</strong><br>
                      <v-img
                        :src="`${mediaUrl}${weeklyReward.image_path}`"
                        height="100"
                        width="100"
                        contain
                        class="mt-2"
                      ></v-img>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Edit Form (Edit Mode) -->
          <template v-if="editMode">
            <!-- Category Selection -->
            <v-row>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.category_id"
                  :items="categories"
                  item-text="description"
                  item-value="id"
                  outlined
                  label="Category *"
                  prepend-inner-icon="mdi-tag"
                  required
                />
              </v-col>
              <v-col sm="6" class="d-flex align-center">
                <v-btn
                  color="success"
                  small
                  @click="save"
                  :loading="isLoading"
                  class="mr-2"
                >
                  <v-icon left>mdi-check</v-icon>
                  Save Changes
                </v-btn>
                <v-btn
                  color="grey"
                  small
                  @click="cancelEdit"
                >
                  <v-icon left>mdi-close</v-icon>
                  Cancel
                </v-btn>
              </v-col>
            </v-row>

            <!-- Reward Details Form -->
            <v-row class="mt-4">
              <v-col sm="3">
                <v-text-field
                  v-model="form.name"
                  outlined
                  label="Reward Name"
                  prepend-inner-icon="mdi-gift"
                />
              </v-col>

              <v-col sm="3">
                <v-text-field
                  v-model="form.coin"
                  outlined
                  label="Coin Value"
                  type="number"
                  prepend-inner-icon="mdi-bitcoin"
                />
              </v-col>

              <v-col sm="3">
                <v-autocomplete
                  v-model="form.store_item_id"
                  :items="storeItems"
                  item-text="name"
                  item-value="id"
                  outlined
                  label="Store Item"
                  prepend-inner-icon="mdi-package-variant"
                  clearable
                />
              </v-col>

              <v-col sm="3">
                <v-text-field
                  v-model="form.expiry_date"
                  outlined
                  label="Expiry Date"
                  type="date"
                  prepend-inner-icon="mdi-calendar-clock"
                />
              </v-col>
            </v-row>

            <!-- Image Upload -->
            <v-row>
              <v-col sm="6">
                <input
                  type="file"
                  id="file-upload"
                  ref="fileUpload"
                  @change="onFileChange"
                  accept="image/*"
                />

                <v-btn
                  x-small
                  class="reset_image my-1"
                  color="red"
                  dark
                  @click="resetImage"
                >
                  Remove Image
                </v-btn>

                <div v-if="form.image_file" class="mt-3">
                  <label>New Image Preview</label>
                  <br />
                  <img
                    :src="form.image_file"
                    style="height: 100px; width: 100px; object-fit: cover"
                  />
                </div>

                <!-- Show existing image if no new image selected -->
                <div v-else-if="weeklyReward.image_path" class="mt-3">
                  <label>Current Image</label>
                  <br />
                  <img
                    :src="`${mediaUrl}${weeklyReward.image_path}`"
                    style="height: 100px; width: 100px; object-fit: cover"
                  />
                </div>
              </v-col>

              <!-- Active Status -->
              <v-col sm="6">
                <v-switch
                  v-model="form.is_active"
                  label="Active Status"
                  inset
                  color="primary"
                ></v-switch>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "WeeklyRewardEditPage",
  data() {
    return {
      weeklyReward: {},
      form: {
        category_id: "",
        name: "",
        coin: "",
        store_item_id: null,
        expiry_date: "",
        is_active: false,
        image_file: null,
        file: null,
      },
      categories: [],
      storeItems: [],
      isLoading: false,
      editMode: false,
      old_json: {},
    };
  },
  mounted() {
    this.getData();
    this.getCategories();
    this.getStoreItems();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.weeklyRewardId;

      try {
        const response = await axios.get(`/weekly_rewards/${encryptedId}`);
        this.weeklyReward = response.data.data;
        this.old_json = response.data.data;

        // Initialize form data
        this.form = {
          category_id: this.weeklyReward.category_id,
          name: this.weeklyReward.name,
          coin: this.weeklyReward.coin,
          store_item_id: this.weeklyReward.store_item_id,
          expiry_date: this.weeklyReward.expiry_date,
          is_active: this.weeklyReward.is_active,
          image_file: null,
          file: null,
        };

      } catch (error) {
        console.error("Error fetching data:", error);
        this.$toast?.error("Failed to load weekly reward data");
      }

      this.isLoading = false;
    },

    async getCategories() {
      try {
        const response = await axios.get("/masters?context=reward_category");
        this.categories = response.data.reward_category || [];
      } catch (e) {
        console.error(e);
      }
    },

    async getStoreItems() {
      try {
        const response = await axios.get("/weekly_rewards/store-items");
        this.storeItems = response.data.data || [];
      } catch (e) {
        console.error(e);
      }
    },

    enterEditMode() {
      this.editMode = true;
    },

    cancelEdit() {
      this.editMode = false;
      // Reset form data
      this.form = {
        category_id: this.weeklyReward.category_id,
        name: this.weeklyReward.name,
        coin: this.weeklyReward.coin,
        store_item_id: this.weeklyReward.store_item_id,
        expiry_date: this.weeklyReward.expiry_date,
        is_active: this.weeklyReward.is_active,
        image_file: null,
        file: null,
      };
    },

    async save() {
      try {
        this.isLoading = true;

        const updateData = {
          category_id: this.form.category_id,
          name: this.form.name,
          coin: this.form.coin,
          store_item_id: this.form.store_item_id,
          expiry_date: this.form.expiry_date,
          is_active: this.form.is_active,
        };

        // Handle file upload
        if (this.form.file) {
          const formData = new FormData();
          Object.keys(updateData).forEach(key => {
            formData.append(key, updateData[key]);
          });
          formData.append('image_path', this.form.file);

          await axios.patch(`/weekly_rewards/${this.$route.params.weeklyRewardId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          await axios.patch(`/weekly_rewards/${this.$route.params.weeklyRewardId}`, updateData);
        }

        await this.saveUserTimestamp();

      } catch (error) {
        console.error(error);
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            this.$toast?.error(errors[key][0]);
          });
        } else {
          this.$toast?.error("Failed to update weekly reward");
        }
      } finally {
        this.isLoading = false;
      }
    },

    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
          this.$toast?.error('Please select a valid image file (JPEG, PNG, JPG, GIF)');
          return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
          this.$toast?.error('File size must be less than 2MB');
          return;
        }

        this.form.file = file;
        this.form.image_file = URL.createObjectURL(file);
      }
    },

    resetImage() {
      this.form.file = null;
      this.form.image_file = null;
      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.value = null;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
      } catch (e) {
        return dateString;
      }
    },

    async saveUserTimestamp() {
      try {
        let form = {
          from_path: `/weekly-rewards/${this.$route.params.weeklyRewardId}`,
          from_name: "WeeklyRewardEditPage",
          user_id: this.user?.id,
          old_json: JSON.stringify(this.old_json),
          new_json: JSON.stringify(this.form),
        };
        await axios.post(`/user_timestamps`, form);
        this.$toast?.success("Weekly Reward updated successfully!");
        this.$router.push("/weekly-rewards");
      } catch (error) {
        console.log(error);
        this.$toast?.success("Updated successfully, but failed to save timestamp");
        this.$router.push("/weekly-rewards");
      }
    },
  },
};
</script>

<style scoped>
input[type="file"] {
  margin-bottom: 10px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
}
.reset_image {
  margin-top: 5px;
}
</style>